import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import { ProductCard } from "@/components/ProductCard";

const CATEGORIES = ["All", "Graphic Tees", "Heavy Oversized", "Acid Wash", "Minimal Archival", "Custom Bags"];
const SORTS = [
  { id: "featured", label: "Featured" },
  { id: "price-asc", label: "Price: low to high" },
  { id: "price-desc", label: "Price: high to low" },
  { id: "name", label: "Name A–Z" },
];

export default function ShopPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("All");
  const [sort, setSort] = useState("featured");

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const visible = useMemo(() => {
    let list = category === "All" ? [...products] : products.filter((p) => p.category === category);
    if (sort === "price-asc") list.sort((a, b) => a.price - b.price);
    if (sort === "price-desc") list.sort((a, b) => b.price - a.price);
    if (sort === "name") list.sort((a, b) => a.name.localeCompare(b.name));
    if (sort === "featured") list.sort((a, b) => Number(b.featured) - Number(a.featured));
    return list;
  }, [products, category, sort]);

  return (
    <main data-testid="shop-page" className="pt-16">
      <Seo
        title="Shop All Heavyweight T-Shirts — Marabou Design"
        description="Browse the full Marabou Design archive: graphic tees, heavy oversized fits, acid wash and minimal archival styles in 250–300 GSM organic cotton."
      />
      <section className="border-b border-[#E4E4DE] py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <motion.p
            className="font-mono text-xs uppercase tracking-[0.25em] text-[#4D7C0F]/90"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            The archive — all drops
          </motion.p>
          <motion.h1
            className="mt-4 font-display text-4xl sm:text-6xl font-extrabold uppercase tracking-tighter leading-[0.9]"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            Shop every tee
          </motion.h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`filter-category-${c.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setCategory(c)}
                className={`px-4 py-2.5 border font-mono text-xs uppercase tracking-widest transition-colors ${
                  category === c
                    ? "border-[#C8FF1E] bg-[#C8FF1E] text-black"
                    : "border-[#E4E4DE] text-zinc-500 hover:border-zinc-500 hover:text-zinc-900"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <select
            data-testid="sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-[#F7F7F2] border border-[#E4E4DE] px-4 py-2.5 font-mono text-xs uppercase tracking-widest text-zinc-600 focus:border-[#C8FF1E] focus:outline-none"
          >
            {SORTS.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <p data-testid="shop-loading" className="py-24 text-center font-mono text-xs uppercase tracking-widest text-zinc-500">
            Loading the archive…
          </p>
        ) : visible.length === 0 ? (
          <p data-testid="shop-empty" className="py-24 text-center font-mono text-xs uppercase tracking-widest text-zinc-500">
            No tees in this category yet
          </p>
        ) : (
          <div data-testid="product-grid" className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
            {visible.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
